/**
 * `items.<index>.<field>` — the product tag dialect the email editor exports
 * and the renderer runs. `items` is bound by an enclosing
 * `{% with items=tile_data.<model> %}` (see `productContexts.ts`); the index is
 * absolute for that model across the whole document, so the chip that reads
 * `items.2.name` really is the third product wherever its context sits.
 */

const PRODUCT_ITEM_IDENTIFIER_PATTERN = /^items\.(\d+)\.([a-z]\w*)$/;

/** Scanning form for finding `items.N.field` references anywhere in text. */
export const PRODUCT_ITEM_TAG_PATTERN = /\bitems\.(\d+)\.[a-z]\w*/g;

export interface ProductItemRef {
  index: number;
  field: string;
}

/** `items.2.name` → `{ index: 2, field: "name" }`, or null for anything else. */
export function parseProductItemIdentifier(
  identifier: string,
): ProductItemRef | null {
  const match = PRODUCT_ITEM_IDENTIFIER_PATTERN.exec(identifier.trim());
  if (!match) return null;
  const index = Number.parseInt(match[1], 10);
  return Number.isFinite(index) && index >= 0
    ? { index, field: match[2] }
    : null;
}

export function productItemIdentifier(index: number, field: string): string {
  return `items.${index}.${field}`;
}

/**
 * Chip/picker label. The `Product →` prefix keeps the label self-describing
 * outside the picker's category heading (chip, typeahead, used-tag row).
 *
 * `modelLabel` (the enclosing context's product model, e.g. "Cart Items")
 * disambiguates the same absolute index across different contexts — a cart's
 * "product 1" reads apart from a discover grid's "product 1". Omitted for the
 * generic picker template, where the model isn't chosen until insert.
 */
export function productItemLabel(
  index: number,
  fieldLabel: string,
  modelLabel?: string,
): string {
  const product = modelLabel
    ? `${modelLabel} product ${index + 1}`
    : `Product ${index + 1}`;
  return `${product} → ${fieldLabel}`;
}

/** Highest `items.N` index referenced in the text, or -1 when none. */
export function maxProductItemIndex(text: string): number {
  let max = -1;
  for (const match of text.matchAll(PRODUCT_ITEM_TAG_PATTERN)) {
    max = Math.max(max, Number.parseInt(match[1], 10));
  }
  return max;
}
